/**
 * Mapping des types de champs entre backend et frontend
 *
 * Backend (ProcedureFields enum dans horodaty-api) :
 * - text, textarea, checkbox, select, date, photo, photo_certified, video_certified, signature
 *
 * Frontend (FormProcedureCreate.jsx) :
 * - text, comment, checkbox, dropdown, date, photo, video
 */

// Types backend (source of truth)
export const BACKEND_FIELD_TYPES = {
    TEXT: 'text',
    TEXTAREA: 'textarea',
    CHECKBOX: 'checkbox',
    SELECT: 'select',
    DATE: 'date',
    PHOTO: 'photo',
    PHOTO_CERTIFIED: 'photo_certified',
    VIDEO_CERTIFIED: 'video_certified',
    SIGNATURE: 'signature',
} as const;

// Types frontend (legacy naming)
export const FRONTEND_FIELD_TYPES = {
    TEXT: 'text',
    COMMENT: 'comment', // Maps to textarea
    CHECKBOX: 'checkbox',
    DROPDOWN: 'dropdown', // Maps to select
    DATE: 'date',
    PHOTO: 'photo',
    VIDEO: 'video', // Maps to video_certified
    SIGNATURE: 'signature',
} as const;

/**
 * Convertit un type frontend vers le type backend
 */
export function frontendToBackendFieldType(frontendType: string): string {
    const mapping: Record<string, string> = {
        text: 'text',
        comment: 'textarea',
        checkbox: 'checkbox',
        dropdown: 'select',
        date: 'date',
        photo: 'photo',
        video: 'video_certified',
        // Backend types passthrough
        textarea: 'textarea',
        select: 'select',
        photo_certified: 'photo_certified',
        video_certified: 'video_certified',
        signature: 'signature',
    };

    return mapping[frontendType] || frontendType;
}

/**
 * Convertit un type backend vers le type frontend pour le rendu
 */
export function backendToFrontendFieldType(backendType: string): string {
    const mapping: Record<string, string> = {
        text: 'text',
        textarea: 'comment',
        checkbox: 'checkbox',
        select: 'dropdown',
        date: 'date',
        photo: 'photo',
        photo_certified: 'photo', // Traité comme photo pour le rendu
        video_certified: 'video', // Traité comme vidéo pour le rendu
        signature: 'signature',
    };

    return mapping[backendType] || backendType;
}

/**
 * Vérifie si un type de champ nécessite des options
 */
export function fieldTypeRequiresOptions(type: string): boolean {
    const backendType = frontendToBackendFieldType(type);
    return backendType === 'checkbox' || backendType === 'select';
}

/**
 * Vérifie si un type de champ est un champ photo
 */
export function isPhotoFieldType(type: string): boolean {
    const backendType = frontendToBackendFieldType(type);
    return backendType === 'photo' || backendType === 'photo_certified';
}

/**
 * Vérifie si un type de champ est un champ vidéo
 */
export function isVideoFieldType(type: string): boolean {
    const backendType = frontendToBackendFieldType(type);
    return backendType === 'video_certified';
}

/**
 * Vérifie si un type de champ est un champ signature
 */
export function isSignatureFieldType(type: string): boolean {
    const backendType = frontendToBackendFieldType(type);
    return backendType === 'signature';
}

/**
 * Retourne le type HTML input correspondant
 */
export function getHtmlInputType(type: string): string {
    const backendType = frontendToBackendFieldType(type);
    const mapping: Record<string, string> = {
        text: 'text',
        textarea: 'textarea',
        checkbox: 'checkbox',
        select: 'select',
        date: 'date',
        photo: 'file',
        photo_certified: 'file',
        video_certified: 'file',
        signature: 'text',
    };

    return mapping[backendType] || 'text';
}

/**
 * Définition des types de champs avec leurs métadonnées
 */
export const FIELD_TYPE_DEFINITIONS = [
    { id: 'text', backendType: 'text', label: 'Champ texte', icon: 'Type' },
    { id: 'textarea', backendType: 'textarea', label: 'Champ de commentaire', icon: 'MessageSquare' },
    { id: 'checkbox', backendType: 'checkbox', label: 'Cases à cocher', icon: 'CheckSquare' },
    { id: 'select', backendType: 'select', label: 'Liste déroulante', icon: 'List' },
    { id: 'date', backendType: 'date', label: 'Date', icon: 'Calendar' },
    { id: 'photo', backendType: 'photo', label: 'Photo', icon: 'Camera' },
    { id: 'photo_certified', backendType: 'photo_certified', label: 'Photo certifiée', icon: 'Camera' },
    { id: 'video_certified', backendType: 'video_certified', label: 'Vidéo certifiée', icon: 'Video' },
    { id: 'signature', backendType: 'signature', label: 'Signature', icon: 'Pen' },
] as const;

export type BackendFieldType = (typeof BACKEND_FIELD_TYPES)[keyof typeof BACKEND_FIELD_TYPES];
export type FrontendFieldType = (typeof FRONTEND_FIELD_TYPES)[keyof typeof FRONTEND_FIELD_TYPES];
